import { TitleCasePipe } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { mapTransactionType, mapTweRecurringTransactionType } from '../../../create-request/components/transactions-table/table-helpers';
import { CashieringRequestApr } from '../../../create-request/models/internal-transfer/CashieringRequestApr';
import { RecurringRequest } from '../../../create-request/models/internal-transfer/RecurringRequest';
import { SloaRequest } from '../../../create-request/models/internal-transfer/SloaRequest';
import { AdobeConstants } from '../../adobe-tagging/AdobeConstants';
import { AdobeHelpers } from '../../adobe-tagging/adobeHelper';
import { FormatDatePipe } from '../../pipes/FormatDatePipe';
import { NamePipe } from '../../pipes/NamePipe';
import { TweRecurringTableColumn, TweStandingAuthTableColumn, TweTransactionsTableColumn } from '../../types/TransactionTableColumn';
import { amountFormatter } from '../../utilities/AmountFormatter';
import { Constants, MediaTypeCdDict, MediaTypeCode, transactionDirection, transactionTypes, tweTransactionTypeDict, logoSvg } from '../../utilities/Constants';
import { TaxWithholdingFields, TransactionForm } from '../../../create-request/models/form/TransactionForm';
import { TableComponentState } from '../../types/TableComponentState';
import { StandingAuthFormActions } from '../../../standing-authorizations/actions';
import { selectIsCreateStandingAuth, selectIsCreateTransaction } from '../../reducers';
import { TransactionFormActions } from '../../../create-request/actions';
import { ToastrService } from 'ngx-toastr';
import { AccountInfoActions } from '../../../account-info-header/actions';
import { selectAchInstructions, selectEditTweModalState, selectEditTweServiceError, selectEditTweSubmitCallFinished, selectMasterAccount, selectRegistrationType, selectSubAccountId, selectSubmitDeleteStandingAuthState } from '../../../account-info-header/reducers';
import { ComponentState } from '../../types/ComponentState';
import { deleteTransactionClicked, resetSubmitCancelTransactionState } from '../../../create-request/actions/standing-authorizations-table.actions';
import { Sloa } from '../../../create-request/models';
import { selectAchInfo, selectStagedTransactionForm } from '../../../create-request/reducers';
import { AchInfo } from '../../../create-request/models/ach/AchInfo';
import { selectHasEditAchPhase2 } from '../../../authorize/reducers';
import { FeatureFlagService } from '../../services/feature-flag-service';
import { StandingInstruction } from '../../types/StandingInstruction';

@Component({
  standalone: false,
  selector: 'mm-twe-modal',
  templateUrl: './twe-modal.component.html',
  styleUrls: ['./twe-modal.component.scss']
})
export class TweModalComponent implements OnInit, AfterViewInit {
  @Input() showModal: Observable<boolean>;
  @Input() upcomingTransactions$: Observable<(CashieringRequestApr | RecurringRequest)[]>;
  @Input() sloas$: Observable<SloaRequest[]>;
  @Input() recurringTransactions$: Observable<RecurringRequest[]>;
  @Input() taxWithholdings$: Observable<TaxWithholdingFields>;
  @Input() tweTableState$: Observable<TableComponentState>;
  @Input() isEditAccountLevelTwe: boolean;
  @Output() deleteNewAchProfileFromReview = new EventEmitter<boolean>();
  paragraphOne: string = '';
  paragraphTwo: string = Constants.twePopupParagraphTwo;
  modal: EverestModal;
  modalId: string;
  fedTaxPercent: string;
  stateTaxPercent: string;
  destroy$: Subject<boolean> = new Subject<boolean>();
  upcomingTransactions: TweTransactionsTableColumn[];
  formatDatePipe = new FormatDatePipe();
  standingAuthorizations: TweStandingAuthTableColumn[];
  recurringTransactions: TweRecurringTableColumn[];
  isCreateTransactionPage: boolean;
  isCreateStandingAuthPage: boolean;
  fedTaxRate: number;
  stateTaxRate: number;
  editTweModalState$: Observable<ComponentState>;
  accountId: string;
  submitTweCallFinished: boolean;
  regType: string;
  achInfo: AchInfo;
  transactionForm: TransactionForm;
  logoSvg = logoSvg;
  achInstructions: StandingInstruction[];
  isCancelOrConfirmClicked: boolean = false;

  constructor(private store: Store, private titleCasePipe: TitleCasePipe, private namePipe: NamePipe, private toastr: ToastrService, private ffs: FeatureFlagService,) { }

  ngOnInit(): void {
    this.modalId = 'twe-modal-' + new Date().getTime().toString();


    this.taxWithholdings$.pipe(
      takeUntil(this.destroy$),
    ).subscribe((taxWithholdings) => {
      if (taxWithholdings) {
        this.fedTaxPercent = taxWithholdings.fedTaxPercent;
        this.stateTaxPercent = taxWithholdings.stateTaxPercent;
        this.fedTaxRate = taxWithholdings.fedTaxRate!;
        this.stateTaxRate = taxWithholdings.stateTaxRate!;
        this.paragraphOne = `The updated tax withholding elections you entered (${this.fedTaxPercent} federal and ${this.stateTaxPercent} state) will apply to the transactions and standing authorizations outlined here and become this account's default going forward, excluding qualified transactions.`;
      }
    })

    this.upcomingTransactions$.pipe(
      takeUntil(this.destroy$),
      map((transactions) => this.mapUpcomingTransactionsToRowData(transactions))).subscribe(transactions => {
        this.upcomingTransactions = transactions;
      });

    this.sloas$.pipe(
      map((transactions) => this.mapStandingAuthorizationsToRowData(transactions))).subscribe(standingAuths => {
        this.standingAuthorizations = standingAuths;
      });

    this.recurringTransactions$.pipe(
      map((transactions) => this.mapRecurringTransactionsToRowData(transactions))).subscribe(recurringTrans => {
        this.recurringTransactions = recurringTrans;
      });

    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$)).subscribe((val) => {
      if (val) {
        this.accountId = val;
      }
    });

    this.store.select(selectIsCreateTransaction).pipe(takeUntil(this.destroy$)).subscribe(val => this.isCreateTransactionPage = val);
    this.store.select(selectIsCreateStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(val => this.isCreateStandingAuthPage = val);

    this.editTweModalState$ = this.store.select(selectEditTweModalState);

    this.store.select(selectRegistrationType).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.regType = val;
    })

    this.store.select(selectEditTweSubmitCallFinished).pipe(takeUntil(this.destroy$)).subscribe(callFinished => {
      this.submitTweCallFinished = callFinished;
      if (callFinished) {
        this.hideModal();
        this.toastr.success(Constants.editTweSuccess);
        // Delay the account info refresh to allow the backend time to propagate the updated tax withholding values
        setTimeout(() => {
          // For Inherited IRA accounts we need to call overview to refresh the tax information
          if (this.regType.toLocaleLowerCase().includes('inh')) {
            this.store.dispatch(AccountInfoActions.loadTransactions({ accountId: this.accountId }));
          } else {
            this.store.dispatch(AccountInfoActions.refresh({ accountId: this.accountId }));
          }
        }, 3000);
      }
    });

    this.store.select(selectEditTweServiceError).pipe(takeUntil(this.destroy$)).subscribe(serviceError => {
      if (serviceError.status.toString().startsWith('403')) {
        this.toastr.error(Constants.toastrMessageEmployee);
      } else if (serviceError.status.toString().startsWith('5')) {
        const stateTaxRequiredWhenFedWithheld = serviceError.message?.toUpperCase() === Constants.editTweStateTaxRequiredWhenFedWithheldServiceError;
        const stateTaxBelowMinimum = serviceError.message?.toUpperCase() === Constants.minStateTaxWithholdingError.toUpperCase();
        const toastrMessage = stateTaxRequiredWhenFedWithheld
          ? Constants.editTweErrorStateTaxRequiredWithFedWithholdingMessage
          : stateTaxBelowMinimum
          ? Constants.minStateTaxWithholdingError
          : Constants.editTweError;
        this.toastr.error(toastrMessage, '', { toastClass: 'ngx-toastr toast-error tall-toastr' });
        this.hideModal();
      }
    })

    this.store.select(selectStagedTransactionForm).pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.transactionForm = data;
    });

    this.store.select(selectAchInfo).pipe(takeUntil(this.destroy$)).subscribe(achInfo => {
      this.achInfo = achInfo;
    });

    this.store.select(selectAchInstructions).pipe(takeUntil(this.destroy$)).subscribe(achInstructions => {
      this.achInstructions = achInstructions;
    });

    this.store.select(selectSubmitDeleteStandingAuthState).pipe(takeUntil(this.destroy$)).subscribe(submitDeleteTransaction => {
      if (submitDeleteTransaction.isCancelTransactionCallFinished && submitDeleteTransaction.isNewAchProfile) {
        if (submitDeleteTransaction.isCancelTransactionSubmitted && !submitDeleteTransaction.hasCancelTransactionError) {
          this.toastr.success(Constants.newOnRequestProfileRemoved);
          this.store.dispatch(resetSubmitCancelTransactionState());
        }
      }
    })
  }

  ngAfterViewInit(): void {
    this.modal = document.getElementById(this.modalId) as EverestModal;
    this.modal.addEventListener('closeButtonClick', () => {
      this.showNoTweUpdateToastr();
    });
    this.modal.addEventListener('close', () => {
      if (!this.isCancelOrConfirmClicked) {
        this.showNoTweUpdateToastr();
        this.onCancelClick();
      }
    });
    this.showModal.pipe(takeUntil(this.destroy$)).subscribe((show) => {
      if (show) {
        this.modal.showModal();
        this.modal.focus();
        this.isCancelOrConfirmClicked = false;
      }
    });

  }

  mapRecurringTransactionsToRowData(transactions: RecurringRequest[]): TweRecurringTableColumn[] {
    let recurringTransactions: TweRecurringTableColumn[] = [];
    transactions.forEach(transaction => {
      const trans: TweRecurringTableColumn = {
        Type: mapTweRecurringTransactionType(transaction.recurMediaTypeCode),
        'To/From': this.namePipe.transform(
          `${transaction.recurToPart1} ${transaction.recurToPart2} ${transaction.recurToPart3}`
        ),
        Amount: amountFormatter.format(transaction.recurRequestGrossAmount),
        'Next process date': this.formatDatePipe.transform(transaction.formattedDate),
        Frequency: transaction.frequencyDescription
      }
      recurringTransactions.push(trans);
    });
    return recurringTransactions;
  }

  mapStandingAuthorizationsToRowData(authorizations: SloaRequest[]): TweStandingAuthTableColumn[] {
    let standingAuths: TweStandingAuthTableColumn[] = authorizations.map((authorization) => {
      return {
        'Type': this.titleCasePipe.transform(MediaTypeCdDict[authorization.sloaMediaTypeCode]),
        'To/From': this.namePipe.transform(`${authorization.sloaToPart1} ${authorization.sloaToPart2} ${authorization.sloaToPart3}`),
        'Transfer Direction': authorization.sloaDirection === 'I' ? 'Incoming' : 'Outgoing'
      }
    });

    return standingAuths;
  }

  mapUpcomingTransactionsToRowData(transactions: (CashieringRequestApr | RecurringRequest)[]): TweTransactionsTableColumn[] {
    return transactions.map((transaction) => {
      return this.mapUpcomingTransactions(transaction)
    });
  }

  mapUpcomingTransactions(transaction: CashieringRequestApr | RecurringRequest): TweTransactionsTableColumn {
    if (this.instanceOfCashiering(transaction)) {
      return {
        Type: mapTransactionType(
          transaction.typeDescription,
          transaction.direction === 'O' ? transactionDirection.out : transactionDirection.in
        ),
        Amount: amountFormatter.format(transaction.cashrRequestGrossAmount),
        'To/From': this.titleCasePipe.transform(
          `${transaction.toPart1} ${transaction.toPart2} ${transaction.toPart3}`
        ),
        'Process date': transaction.typeDescription.includes('Recurring') ? '' : this.formatDatePipe.transform(transaction.formattedDate)
      };
    }
    else {
      return {
        Type: mapTransactionType(
          tweTransactionTypeDict[transaction.recurMediaTypeCode],
          transaction.recurDirection === 'O' ? transactionDirection.out : transactionDirection.in
        ),
        Amount: amountFormatter.format(transaction.recurRequestGrossAmount),
        'To/From': this.titleCasePipe.transform(
          `${transaction.recurToPart1} ${transaction.recurToPart2} ${transaction.recurToPart3}`
        ),
        'Process date': this.formatDatePipe.transform(transaction.formattedDate)
      };
    }
  }

  instanceOfCashiering(transaction: CashieringRequestApr | RecurringRequest): transaction is CashieringRequestApr {
    return ('cashrRequestId' in transaction);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onCancelClick(): void {
    this.isCancelOrConfirmClicked = true;
    if (this.isEditAccountLevelTwe) {
      this.toastr.info(Constants.editTweNoUpdate);
    }
    this.processNewProfileCancellation(true);
    this.hideModal();
  }

  onConfirmClick(): void {
    this.isCancelOrConfirmClicked = true;
    AdobeHelpers.fireClick(AdobeConstants.LINKID + AdobeConstants._CONFIRM + AdobeConstants._TWE_CHANGE);
    if (this.isEditAccountLevelTwe) {
      this.store.dispatch(AccountInfoActions.submitTaxWithholdings({ fedTaxRate: this.fedTaxRate, stateTaxRate: this.stateTaxRate }));
    }
    else if (this.isCreateTransactionPage) {
      this.store.dispatch(TransactionFormActions.loadReviewPage());
      this.hideModal();
    } else if (this.isCreateStandingAuthPage) {
      this.store.dispatch(StandingAuthFormActions.loadReviewPage());
      this.hideModal();
    }
  }

  hideModal() {
    this.modal.hideModal(); //in separate function in order to mock it in unit tests
  }

  showNoTweUpdateToastr() {
    if (this.isEditAccountLevelTwe && !this.submitTweCallFinished) {
      this.toastr.info(Constants.editTweNoUpdate);
    }
  }

  onPrintClicked(): void {
    // Match styling on the printable document to the modal
    let styles =
      `span {
        font-family: Arial;
        font-size: 9.1px;
        letter-spacing: 0.063px;
        line-height: 14px;
        display: inline-block;
      }
      .twe-tables-container {
        border: 1.4px solid #e3e3e3;
        background-color: #ffffff;
        margin-top: 11.2px;
        margin-bottom: 5.6px;
        padding: 16.8px;
      }
      .section-title {
        font-family: Arial;
        font-size: 16.8px;
        letter-spacing: 0.105px;
        line-height: 22.4px;
        display: inline-block;
        margin-bottom: 16.8px;
      }
      .subtitle {
        font-family: Arial;
        font-size: 9.1px;
        letter-spacing: 0.07px;
        line-height: 19.6px;
        display: inline-block;
      }
      .label-container {
        width: 63px;
        border-bottom-width: 0.25rem;
        border-bottom-color: #006e99;
        border-bottom-style: solid;
        text-align: center;
        width: fit-content;
      }
      .tab-label {
        padding: 9.8px 11.2px 11.2px 9.8px;
      }
      .table-count {
        font-family: "Arial";
        font-size: 9.1px;
        letter-spacing: 0;
        margin-top: 11.2px;
      }
      td, th {
        font-family: Arial;
        font-size: 9.1px;
        letter-spacing: 0.35px;
        display: inline-block;
        width: 126px;
        line-height: 32.2px;
        padding-left: 16.1px;
        padding-right: 16.1px;
        text-align: left;
      }
      th {
        font-weight: bold;
      }
      tr {
        border-color: #e2e2e2;
        border-bottom-style: solid;
        border-width: 0.025px;
        box-sizing: border-box;
        border-collapse: collapse;
        display: block;
        width: 645px;
      }
      .header-row {
        border-color: #e2e2e2;
        border-top-style: solid;
        border-width: 0.025px;
        box-sizing: border-box;
        border-collapse: collapse;
      }
      .wrap-column {
        white-space: pre-line;
        line-height: 20px;
      }
      .to-from-column {
        width: 200px !important;
      }
      .auto-width {
        width: auto;
      }
      .med-column {
        width: 80px;
      }
      .small-column {
        width: 60px;
      }`;

    // Open a new popup window to print from since you can't do it the nornmal way with a modal
    const printWindow = window.open(' ', '', 'height=900,width=900');
    if (printWindow !== null) {
      printWindow.document.write('<html><head><style>' + styles + '</style>');
      printWindow.document.write(`<title>Move Money - Schwab Advisor Center</title></head>`);
      printWindow.document.write(this.logoSvg);
      printWindow.document.write(`<div><span>${this.paragraphOne}</span></div><br>`);
      printWindow.document.write(`<div><span>${this.paragraphTwo}</span></div>`);
      printWindow.document.write(`<div class="twe-tables-container">`);
      printWindow.document.write(`<div><span class="section-title">Account transactions</span></div>`);
      printWindow.document.write(`<div class="label-container"><span class="tab-label">Upcoming</span></div>`);
      printWindow.document.write(`<div class="table-count"><span><strong>${this.upcomingTransactions.length} result(s)</strong></span></div>`);
      printWindow.document.write(
        `<table>
          <COLGROUP>
          <THEAD>
            <tr class="header-row">
              <th>Type</th>
              <th class="to-from-column">To/From</th>
              <th class="med-column">Process date</th>
              <th class="small-column">Amount</th>
            </tr>
          <THEAD>
          ${this.upcomingTransactions.map(trans =>
          `<TBODY>
              <tr>
                <td>${trans.Type}</td>
                <td class="to-from-column">${trans['To/From']}</td>
                <td class="med-column">${trans['Process date']}</td>
                <td class="small-column">${trans.Amount}</td>
              </tr>
            <TBODY>`
        ).join('')}
      </table>`);
      printWindow.document.write(`<div class="label-container"><span class="tab-label">Standing Authorizations</span></div>`);
      printWindow.document.write(`<div class="table-count"><span><strong>${this.standingAuthorizations.length} result(s)</strong></span></div>`);
      printWindow.document.write(
        `<table>
          <COLGROUP>
          <THEAD>
            <tr class="header-row">
              <th>Type</th>
              <th class="to-from-column">To/From</th>
              <th>Transfer Direction</th>
            </tr>
          <THEAD>
          ${this.standingAuthorizations.map(trans =>
          `<TBODY>
              <tr>
                <td>${trans.Type}</td>
                <td class="to-from-column">${trans['To/From']}</td>
                <td>${trans['Transfer Direction']}</td>
              </tr>
            <TBODY>`
        ).join('')}
      </table>`);
      printWindow.document.write(`<div class="label-container"><span class="tab-label">Recurring</span></div>`);
      printWindow.document.write(`<div class="table-count"><span><strong>${this.recurringTransactions.length} result(s)</strong></span></div>`);
      printWindow.document.write(
        `<table>
          <COLGROUP>
          <THEAD>
            <tr class="header-row">
              <th class="small-column">Type</th>
              <th class="to-from-column">To/From</th>
              <th class="small-column">Amount</th>
              <th class="med-column wrap-column">Next process date</th>
              <th class="auto-width">Frequency</th>
            </tr>
          <THEAD>
          ${this.recurringTransactions.map(trans =>
          `<TBODY>
              <tr>
                <td class="small-column">${trans.Type}</td>
                <td class="to-from-column">${trans['To/From']}</td>
                <td class="small-column">${trans.Amount}</td>
                <td class="med-column">${trans['Next process date']}</td>
                <td class="auto-width">${trans.Frequency}</td>
              </tr>
            <TBODY>`
        ).join('')}
      </table>`);
      printWindow.document.write(`</div>`)
      printWindow.document.write(`</html>`)

      printWindow.window.print();
      printWindow.window.close();
    }
  }

  isNewOnRequestAchProfileCreated(): boolean {
    if (this.transactionForm.transactionType === transactionTypes.ach) {
      return !this.achInstructions.some(instruction => instruction.cashieringRqstId === this.achInfo.cashieringRequestId)
    }
    return false;
  }

  processNewProfileCancellation(checkIfCancelSucceeds: boolean): void {
    if (this.isNewOnRequestAchProfileCreated()) {
      this.store.dispatch(deleteTransactionClicked({ transaction: this.getAchProfileToCancel(), shouldRetry: true, isNewAchProfile: true }));
      this.deleteNewAchProfileFromReview.emit(checkIfCancelSucceeds);
    }
  }
  getAchProfileToCancel(): Sloa {
    let masterAccount: string = '';
    this.store.select(selectMasterAccount).pipe(takeUntil(this.destroy$)).subscribe(account => {
      masterAccount = account;
    });

    let profileDetails = {
      cashieringRqstId: this.achInfo?.cashieringRequestId,
      cashieringRqstSecondId: '0',
      faMasterAcct: masterAccount,
      mediaTypeCd: MediaTypeCode.A
    } as Sloa;
    return profileDetails;
  }
}

type EverestModal = HTMLElement & {
  showModal: () => {},
  hideModal: () => {}
};
